/**
 * AdminWorkshopAnalyticsTab - Workshop sales analytics and payout management for admins
 * Features: Total sales, per-practitioner breakdown, pending payouts, fee tracking
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { 
  DollarSign, TrendingUp, Users, ShoppingBag, Clock, CheckCircle,
  Calendar, BarChart3, CreditCard, Wallet, Building, PiggyBank,
  ChevronDown, ChevronUp, Search, BookOpen, Loader2, Download
} from 'lucide-react';

function getCurrentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const AdminWorkshopAnalyticsTab = () => {
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [practitionerBreakdown, setPractitionerBreakdown] = useState([]);
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [salesHistory, setSalesHistory] = useState([]);
  const [topWorkshops, setTopWorkshops] = useState([]);
  
  // Modal states
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState(null);
  const [payoutNotes, setPayoutNotes] = useState('');
  const [processingPayout, setProcessingPayout] = useState(false);
  
  // Expanded rows
  const [expandedPractitioner, setExpandedPractitioner] = useState(null);
  
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  // Generate month options
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };
  
  useEffect(() => {
    fetchAllData();
  }, [selectedMonth]);
  
  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, practitionersRes, payoutsRes, salesRes, topRes] = await Promise.all([
        axios.get(`${API}/workshops/admin/analytics?month=${selectedMonth}`, { headers }),
        axios.get(`${API}/workshops/admin/practitioner-breakdown?month=${selectedMonth}`, { headers }),
        axios.get(`${API}/workshops/admin/pending-payouts?month=${selectedMonth}`, { headers }),
        axios.get(`${API}/workshops/admin/sales-history?month=${selectedMonth}`, { headers }),
        axios.get(`${API}/workshops/admin/top-workshops?month=${selectedMonth}`, { headers })
      ]);
      
      setAnalytics(analyticsRes.data);
      setPractitionerBreakdown(practitionersRes.data);
      setPendingPayouts(payoutsRes.data);
      setSalesHistory(salesRes.data);
      setTopWorkshops(topRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };
  
  const handleMarkPaid = async () => {
    if (!selectedPractitioner) return;
    
    setProcessingPayout(true);
    try {
      await axios.post(`${API}/workshops/admin/mark-paid`, {
        user_id: selectedPractitioner.user_id,
        month: selectedMonth,
        notes: payoutNotes
      }, { headers });
      
      toast.success(`Payout marked as paid for ${selectedPractitioner.practitioner_name}`);
      setShowPayoutModal(false);
      setSelectedPractitioner(null);
      setPayoutNotes('');
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payout');
    } finally {
      setProcessingPayout(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="admin-workshop-analytics">
      {/* Header with Month Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-cyan-400" />
            Workshop Analytics
          </h2>
          <p className="text-gray-400">Sales, earnings, and payout management</p>
        </div>
        
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-56 text-white">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {getMonthOptions().map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Sales */}
        <Card className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border-purple-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-300">Total Sales</p>
                <p className="text-2xl font-bold text-white">{analytics?.total_sales || 0}</p>
              </div>
              <ShoppingBag className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        
        {/* Total Revenue */}
        <Card className="bg-gradient-to-br from-green-900/30 to-green-800/20 border-green-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-300">Total Revenue</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(analytics?.total_revenue)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        
        {/* Platform Fees */}
        <Card className="bg-gradient-to-br from-cyan-900/30 to-cyan-800/20 border-cyan-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-cyan-300">Platform Fees</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(analytics?.platform_fees)}</p>
              </div>
              <Building className="w-8 h-8 text-cyan-400" />
            </div>
          </CardContent>
        </Card>
        
        {/* Processing Fees */}
        <Card className="bg-gradient-to-br from-orange-900/30 to-orange-800/20 border-orange-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-300">Processing Fees</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(analytics?.processing_fees)}</p>
              </div>
              <CreditCard className="w-8 h-8 text-orange-400" />
            </div>
          </CardContent>
        </Card>
        
        {/* Practitioner Earnings */}
        <Card className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border-blue-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-300">To Practitioners</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(analytics?.practitioner_earnings)}</p>
              </div>
              <Users className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="practitioners">By Practitioner</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
          <TabsTrigger value="sales">Sales History</TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Top Selling Workshops */}
          <Card className="border-slate-700 bg-slate-800/50">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                Top Selling Workshops
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topWorkshops.length > 0 ? (
                <div className="space-y-3">
                  {topWorkshops.map((workshop, index) => (
                    <div 
                      key={workshop.workshop_id}
                      className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${
                          index === 0 ? 'bg-yellow-500 text-black' :
                          index === 1 ? 'bg-gray-400 text-black' :
                          index === 2 ? 'bg-amber-600 text-white' :
                          'bg-slate-600 text-white'
                        }`}>
                          {index + 1}
                        </span>
                        <div>
                          <p className="font-medium text-white">{workshop.title}</p>
                          <p className="text-sm text-gray-400">{workshop.practitioner_names}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-green-400">{formatCurrency(workshop.total_revenue)}</p>
                        <p className="text-sm text-gray-500">{workshop.sales_count} sales</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400 py-8">No sales this month</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* By Practitioner Tab */}
        <TabsContent value="practitioners">
          <Card className="border-slate-700 bg-slate-800/50">
            <CardHeader>
              <CardTitle className="text-lg text-white">Practitioner Breakdown</CardTitle>
              <CardDescription>Earnings per practitioner for {getMonthOptions().find(o => o.value === selectedMonth)?.label}</CardDescription>
            </CardHeader>
            <CardContent>
              {practitionerBreakdown.length > 0 ? (
                <div className="space-y-2">
                  {practitionerBreakdown.map((practitioner) => (
                    <div key={practitioner.user_id} className="border border-slate-700 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedPractitioner(
                          expandedPractitioner === practitioner.user_id ? null : practitioner.user_id
                        )}
                        className="w-full flex items-center justify-between p-4 bg-slate-700/30 hover:bg-slate-700/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <Users className="w-5 h-5 text-cyan-400" />
                          <span className="font-medium text-white">{practitioner.practitioner_name}</span>
                          <Badge variant="outline">{practitioner.workshops_sold} workshops</Badge>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-green-400 font-bold">{formatCurrency(practitioner.total_earnings)}</span>
                          {expandedPractitioner === practitioner.user_id ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </button>
                      
                      {expandedPractitioner === practitioner.user_id && (
                        <div className="p-4 bg-slate-800 border-t border-slate-700">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-slate-700">
                                <TableHead className="text-gray-400">Workshop</TableHead>
                                <TableHead className="text-gray-400 text-right">Sales</TableHead>
                                <TableHead className="text-gray-400 text-right">Share %</TableHead>
                                <TableHead className="text-gray-400 text-right">Earnings</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {practitioner.workshops?.map((workshop) => (
                                <TableRow key={workshop.workshop_id} className="border-slate-700">
                                  <TableCell className="text-white">{workshop.title}</TableCell>
                                  <TableCell className="text-right text-gray-300">{workshop.sales_count}</TableCell>
                                  <TableCell className="text-right text-gray-300">{workshop.percentage}%</TableCell>
                                  <TableCell className="text-right text-green-400">{formatCurrency(workshop.earnings)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-400 py-8">No practitioner earnings this month</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Payouts Tab */}
        <TabsContent value="payouts">
          <Card className="border-slate-700 bg-slate-800/50">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Wallet className="w-5 h-5 text-yellow-400" />
                Pending Payouts
              </CardTitle>
              <CardDescription>Mark payouts as paid when processed</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingPayouts.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-gray-400">Practitioner</TableHead>
                      <TableHead className="text-gray-400 text-right">Pending Amount</TableHead>
                      <TableHead className="text-gray-400 text-right">Sales</TableHead>
                      <TableHead className="text-gray-400">Status</TableHead>
                      <TableHead className="text-gray-400 text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingPayouts.map((payout) => (
                      <TableRow key={payout.user_id} className="border-slate-700">
                        <TableCell className="text-white font-medium">{payout.practitioner_name}</TableCell>
                        <TableCell className="text-right text-yellow-400 font-bold">
                          {formatCurrency(payout.pending_amount)}
                        </TableCell>
                        <TableCell className="text-right text-gray-300">{payout.sales_count}</TableCell>
                        <TableCell>
                          <Badge className="bg-yellow-600 text-white">
                            <Clock className="w-3 h-3 mr-1" /> Pending
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedPractitioner(payout);
                              setShowPayoutModal(true);
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Mark Paid
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <p className="text-gray-400">No pending payouts for this month</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Sales History Tab */}
        <TabsContent value="sales">
          <Card className="border-slate-700 bg-slate-800/50">
            <CardHeader>
              <CardTitle className="text-lg text-white">Sales History</CardTitle>
              <CardDescription>All workshop purchases for {getMonthOptions().find(o => o.value === selectedMonth)?.label}</CardDescription>
            </CardHeader>
            <CardContent>
              {salesHistory.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-gray-400">Date</TableHead>
                        <TableHead className="text-gray-400">Workshop</TableHead>
                        <TableHead className="text-gray-400">Buyer</TableHead>
                        <TableHead className="text-gray-400 text-right">Total</TableHead>
                        <TableHead className="text-gray-400 text-right">Platform Fee</TableHead>
                        <TableHead className="text-gray-400 text-right">Processing</TableHead>
                        <TableHead className="text-gray-400 text-right">To Practitioner</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {salesHistory.map((sale) => (
                        <TableRow key={sale.id} className="border-slate-700">
                          <TableCell className="text-white">{formatDate(sale.purchase_date)}</TableCell>
                          <TableCell className="text-white">{sale.workshop_title}</TableCell>
                          <TableCell className="text-gray-400">{sale.buyer_name}</TableCell>
                          <TableCell className="text-right text-white">{formatCurrency(sale.total_price)}</TableCell>
                          <TableCell className="text-right text-cyan-400">{formatCurrency(sale.platform_fee)}</TableCell>
                          <TableCell className="text-right text-orange-400">{formatCurrency(sale.processing_fee)}</TableCell>
                          <TableCell className="text-right text-green-400">{formatCurrency(sale.practitioner_amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-center text-gray-400 py-8">No sales this month</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Mark Paid Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Payout</DialogTitle>
            <DialogDescription>
              Mark workshop earnings as paid for {selectedPractitioner?.practitioner_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-800 p-4 rounded-lg">
              <p className="text-sm text-gray-400">Amount to pay</p>
              <p className="text-2xl font-bold text-green-400">
                {formatCurrency(selectedPractitioner?.pending_amount)}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Payment Notes (Optional)</Label>
              <Textarea
                placeholder="Payment reference, date, method, etc."
                value={payoutNotes}
                onChange={(e) => setPayoutNotes(e.target.value)}
                className="bg-slate-800 border-slate-700"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayoutModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkPaid}
              disabled={processingPayout}
              className="bg-green-600 hover:bg-green-700"
            >
              {processingPayout ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-2" />
              )}
              Confirm Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminWorkshopAnalyticsTab;
